import React from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";

/**
 * AdminSetup component
 *
 * This component allows setting up the initial admin user.
 * It should only be accessible to the first user who logs in,
 * or to existing admins.
 */
const AdminSetup: React.FC = () => {
  const { currentUser, isAdmin } = useAuth();

  // Check if current user is already an admin
  const userIsAdmin = isAdmin();

  // If the user is already an admin, show a message
  if (userIsAdmin) {
    return (
      <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: "auto", mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Admin Setup
        </Typography>
        <Alert severity="info">
          You are already an admin. You can manage users and permissions through
          the admin panel.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: "auto", mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Admin Setup
      </Typography>

        <>
          <Typography variant="body1" paragraph>
            Set up the initial admin user for this application. As an admin, you
            will have full access to manage users and data.
          </Typography>

          <Typography variant="body2" paragraph>
            To set up an admin user, you need to use the command-line tool:
          </Typography>

          <Box
            sx={{ bgcolor: "background.paper", p: 2, borderRadius: 1, mb: 2 }}
          >
            <Typography
              variant="body2"
              component="pre"
              sx={{ fontFamily: "monospace" }}
            >
              node setup-admin.js &lt;email&gt; &lt;uid&gt;
              &lt;service-account-key-path&gt;
            </Typography>
          </Box>

          <Typography variant="body2" paragraph>
            Follow these steps:
          </Typography>

          <ol>
            <li>
              <Typography variant="body2" paragraph>
                Create a service account key in Firebase Console (Project
                Settings &gt; Service accounts)
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                Run the setup script with your email, UID, and the path to the
                service account key
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                Refresh this page after running the script
              </Typography>
            </li>
          </ol>

          <Typography variant="body2" paragraph>
            Your UID is: <strong>{currentUser?.uid}</strong>
          </Typography>
        </>
    </Paper>
  );
};

export default AdminSetup;
